import { useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { api } from '../../lib/api';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const GAME_API_BASE = import.meta.env.VITE_GAME_API_URL || '/api';

/** Đếm ngược, gọi onExpire khi hết giờ */
function CountdownTimer({ initialSec, onExpire }: { initialSec: number; onExpire: () => void }) {
    const [remaining, setRemaining] = useState(initialSec);
    const onExpireRef = useRef(onExpire);
    onExpireRef.current = onExpire;

    useEffect(() => {
        if (initialSec <= 0) { onExpireRef.current(); return; }
        const t = setInterval(() => {
            setRemaining(s => {
                if (s <= 1) { clearInterval(t); setTimeout(() => onExpireRef.current(), 0); return 0; }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(t);
    }, [initialSec]);

    const h   = Math.floor(remaining / 3600);
    const m   = Math.floor((remaining % 3600) / 60);
    const sec = remaining % 60;
    const fmt = h > 0
        ? `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
        : `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;

    return (
        <div className="mt-2">
            <div style={{ fontSize: '2rem', fontWeight: 900, color: '#FFD700', fontVariantNumeric: 'tabular-nums' }}>
                {fmt}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,.35)' }}>thời gian còn lại</div>
        </div>
    );
}

const LOCAL_GAMES: Record<string, string> = {
    'lucky': '/lucky-wheel/index.html',
    'wheel': '/lucky-wheel/index.html',
    'scratch': '/scratch-card/index.html',
    'flip': '/flip-card/index.html',
    'egg': '/egg-pick/index.html',
    'trung': '/trung-thu/index.html',
};

function buildGameFile(game: any, isTemplate: boolean): string {
    const template = isTemplate ? game : game?.game_template;
    const runtimeUrl = isTemplate ? game?.runtime_url : template?.runtime_url;
    if (runtimeUrl && runtimeUrl.startsWith('/') && !runtimeUrl.startsWith('//')) return runtimeUrl;
    const code = String(template?.code || '').toLowerCase();
    for (const [key, path] of Object.entries(LOCAL_GAMES)) {
        if (code.includes(key)) return path;
    }
    return '/lucky-wheel/index.html';
}

function buildPreviewUrl(game: any, isTemplate: boolean, playerId?: string): string {
    const gameFile = buildGameFile(game, isTemplate);
    const qp = new URLSearchParams({ apiBase: GAME_API_BASE });
    if (!isTemplate && game?.campaign?.slug) {
        qp.set('campaignSlug', game.campaign.slug);
        qp.set('gameInstanceId', game.id || '');
    }
    if (playerId) qp.set('playerId', playerId);
    qp.set('t', Date.now().toString()); // Cache buster
    return `${gameFile}?${qp.toString()}`;
}

// ─── Dynamic Form Field Renderer ─────────────────────────────────────────────
interface FormFieldDef {
    key: string;
    label: string;
    type: 'text' | 'tel' | 'email' | 'number' | 'textarea' | 'select' | 'checkbox';
    required?: boolean;
    placeholder?: string;
    options?: string[];
    description?: string;    // HTML fallback
    intro_text?: string;     // câu dẫn
    terms_label?: string;
    terms_url?: string;
    terms_content?: string;
    privacy_label?: string;
    privacy_url?: string;
    privacy_content?: string;
}

// ─── Theme Palette (khớp với game engine) ────────────────────────────────────
const FORM_THEMES: Record<string, {
    cardBg: string;
    cardBorder: string;
    title: string;
    inputFocus: string;
    submitBg: string;
    submitColor: string;
    overlayBg: string;
}> = {
    red:       { cardBg: 'linear-gradient(160deg,#1a0505,#350a0a)', cardBorder: 'rgba(255,215,0,.4)',   title: '#ffd700', inputFocus: 'rgba(255,215,0,.6)',   submitBg: 'linear-gradient(135deg,#ffd700,#daa520)', submitColor: '#1a0800', overlayBg: 'rgba(20,5,5,0.96)' },
    blue:      { cardBg: 'linear-gradient(160deg,#020617,#0c1445)', cardBorder: 'rgba(0,240,255,.4)',   title: '#22d3ee', inputFocus: 'rgba(0,240,255,.6)',   submitBg: 'linear-gradient(135deg,#0ea5e9,#0284c7)', submitColor: '#ffffff', overlayBg: 'rgba(2,6,23,0.96)' },
    gold:      { cardBg: 'linear-gradient(160deg,#1a1000,#2d1f00)', cardBorder: 'rgba(255,215,0,.45)',  title: '#ffd700', inputFocus: 'rgba(255,215,0,.65)',  submitBg: 'linear-gradient(135deg,#ffd700,#daa520)', submitColor: '#1a0800', overlayBg: 'rgba(16,10,0,0.96)' },
    green:     { cardBg: 'linear-gradient(160deg,#021207,#04230f)', cardBorder: 'rgba(74,222,128,.4)',  title: '#86efac', inputFocus: 'rgba(74,222,128,.65)', submitBg: 'linear-gradient(135deg,#22c55e,#16a34a)', submitColor: '#ffffff', overlayBg: 'rgba(2,12,7,0.96)' },
    cyberpunk: { cardBg: 'linear-gradient(160deg,#050015,#0d002a)', cardBorder: 'rgba(0,255,255,.5)',   title: '#00ffff', inputFocus: 'rgba(0,255,255,.7)',   submitBg: 'linear-gradient(135deg,#00ffff,#0080ff)', submitColor: '#050015', overlayBg: 'rgba(5,0,21,0.96)' },
    sunset:    { cardBg: 'linear-gradient(160deg,#1e0f07,#3b1e0f)', cardBorder: 'rgba(249,115,22,.4)',   title: '#fdba74', inputFocus: 'rgba(249,115,22,.6)',   submitBg: 'linear-gradient(135deg,#f97316,#ea580c)', submitColor: '#ffffff', overlayBg: 'rgba(20,10,5,0.96)' },
    violet:    { cardBg: 'linear-gradient(160deg,#0d061e,#1c0e3a)', cardBorder: 'rgba(139,92,246,.5)',   title: '#c084fc', inputFocus: 'rgba(139,92,246,.7)',   submitBg: 'linear-gradient(135deg,#8b5cf6,#7c3aed)', submitColor: '#ffffff', overlayBg: 'rgba(10,5,20,0.96)' },
};
// Backward-compat: 'neon' was the old theme ID before rename to 'cyberpunk'
(FORM_THEMES as any)['neon'] = FORM_THEMES['cyberpunk'];
(FORM_THEMES as any)['default'] = FORM_THEMES['blue'];
const DEFAULT_THEME = FORM_THEMES['blue'];

const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,.07)',
    border: '1.5px solid rgba(255,255,255,.15)',
    color: '#fff',
    width: '100%',
    padding: '10px 12px',
    borderRadius: '12px',
    fontSize: '14px',
    marginBottom: '10px',
    outline: 'none',
    boxSizing: 'border-box',
};

// ─── Content Modal ────────────────────────────────────────────────────────────
function ContentModal({ title, content, url, onClose }: {
    title: string;
    content?: string;
    url?: string;
    onClose: () => void;
}) {
    return (
        <div style={{
            position: 'absolute', inset: 0, zIndex: 200,
            background: 'rgba(5,5,20,0.97)',
            display: 'flex', flexDirection: 'column',
        }}>
            {/* Header */}
            <div style={{
                padding: '14px 16px',
                borderBottom: '1px solid rgba(255,215,0,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'linear-gradient(160deg,#0f0f2e,#1a1a3e)',
                flexShrink: 0,
            }}>
                <span style={{ color: '#FFD700', fontWeight: 700, fontSize: '13px' }}>📄 {title}</span>
                <button
                    onClick={onClose}
                    style={{ color: '#9ca3af', fontSize: '22px', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }}
                >×</button>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                {content ? (
                    <pre style={{
                        color: '#d1d5db', fontSize: '12px', lineHeight: '1.8',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', margin: 0,
                    }}>{content}</pre>
                ) : url ? (
                    <div style={{ textAlign: 'center', paddingTop: '40px' }}>
                        <p style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '16px' }}>Xem nội dung đầy đủ tại:</p>
                        <a href={url} target="_blank" rel="noopener"
                            style={{ color: '#60a5fa', fontSize: '13px', wordBreak: 'break-all' }}>{url}</a>
                    </div>
                ) : null}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 16px', flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <button
                    onClick={onClose}
                    style={{
                        width: '100%', padding: '10px', borderRadius: '12px', border: 'none',
                        background: 'linear-gradient(135deg,#FFD700,#ffe066)', color: '#1a0a00',
                        fontWeight: 700, fontSize: '13px', cursor: 'pointer',
                    }}
                >✓ Đã đọc, đóng lại</button>
            </div>
        </div>
    );
}

// ─── Checkbox Link — smart link render ───────────────────────────────────────
function CheckboxLink({ label, url, content, onShowModal, theme }: {
    label: string;
    url?: string;
    content?: string;
    onShowModal: (title: string, content?: string, url?: string) => void;
    theme: any;
}) {
    const hasContent = content && content.trim().length > 0;
    const hasUrl = url && url.trim().length > 0 && url !== '#';

    if (!hasContent && !hasUrl) {
        return <span style={{ color: theme.title, textDecoration: 'underline' }}>{label}</span>;
    }

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // prevent checkbox toggle
        if (hasContent) {
            onShowModal(label, content, undefined);
        } else if (hasUrl) {
            window.open(url, '_blank', 'noopener');
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            style={{
                color: theme.title, textDecoration: 'underline',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 0, fontSize: 'inherit', fontFamily: 'inherit',
                display: 'inline',
            }}
        >
            {label}
            {hasContent && <span style={{ fontSize: '10px', marginLeft: '2px', color: theme.title }}>📄</span>}
        </button>
    );
}

function DynamicField({ field, value, onChange, onShowModal, theme }: {
    field: FormFieldDef;
    value: any;
    onChange: (key: string, val: any) => void;
    onShowModal: (title: string, content?: string, url?: string) => void;
    theme: any;
}) {
    const label = (
        <label className="preview-form-label" style={{ display: 'block', fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}>
            {field.label}{field.required && <span style={{ color: '#f87171', marginLeft: '2px' }}>*</span>}
        </label>
    );

    if (field.type === 'select') {
        return (
            <div>
                {label}
                <select
                    className="preview-form-input"
                    value={value || ''}
                    onChange={e => onChange(field.key, e.target.value)}
                    style={{ ...inputStyle, appearance: 'auto', colorScheme: 'dark' }}
                >
                    <option value="" style={{ background: '#222', color: '#fff' }}>-- Chọn {field.label} --</option>
                    {(field.options || []).map(o => (
                        <option key={o} value={o} style={{ background: '#222', color: '#fff' }}>{o}</option>
                    ))}
                </select>
            </div>
        );
    }

    if (field.type === 'textarea') {
        return (
            <div>
                {label}
                <textarea
                    className="preview-form-input"
                    value={value || ''}
                    onChange={e => onChange(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    rows={3}
                    style={{ ...inputStyle, resize: 'none' }}
                />
            </div>
        );
    }

    if (field.type === 'checkbox') {
        const intro       = field.intro_text   || 'Tôi đã đọc và đồng ý với';
        const termsLabel  = field.terms_label  || 'Thể lệ chương trình';
        const privacyLabel= field.privacy_label|| 'Chính sách bảo mật';
        const hasTerms   = field.terms_label   || field.terms_url   || field.terms_content;
        const hasPrivacy = field.privacy_label || field.privacy_url || field.privacy_content;

        const checkboxText = hasTerms || hasPrivacy ? (
            <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}>
                {intro}{' '}
                {hasTerms && (
                    <CheckboxLink
                        label={termsLabel}
                        url={field.terms_url}
                        content={field.terms_content}
                        onShowModal={onShowModal}
                        theme={theme}
                    />
                )}
                {hasTerms && hasPrivacy && ' và '}
                {hasPrivacy && (
                    <CheckboxLink
                        label={privacyLabel}
                        url={field.privacy_url}
                        content={field.privacy_content}
                        onShowModal={onShowModal}
                        theme={theme}
                    />
                )}
                .
            </span>
        ) : (
            <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}>
                {field.label}{field.required && <span style={{ color: '#f87171' }}> *</span>}
            </span>
        );

        return (
            <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={!!value}
                        onChange={e => onChange(field.key, e.target.checked)}
                        style={{ marginTop: '3px', flexShrink: 0, accentColor: theme.title, width: '15px', height: '15px' }}
                    />
                    {checkboxText}
                </label>
            </div>
        );
    }

    return (
        <div>
            {label}
            <input
                className="preview-form-input"
                type={field.type}
                value={value || ''}
                onChange={e => onChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                style={inputStyle}
            />
        </div>
    );
}

// ─── Default fields nếu campaign không cấu hình ──────────────────────────────
const DEFAULT_FIELDS: FormFieldDef[] = [
    { key: 'name',  label: 'Họ và tên',      type: 'text', required: true, placeholder: 'Nguyễn Văn A' },
    { key: 'phone', label: 'Số điện thoại',  type: 'tel',  required: true, placeholder: '0912 345 678' },
    { key: 'email', label: 'Email',           type: 'email',required: false, placeholder: 'email@example.com' },
];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function GamePreview() {
    const { id } = useParams();
    const location = useLocation();
    const isTemplate = location.pathname.includes('/preview/template/');

    const [game, setGame] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState<'before' | 'after_win'>('before');
    const [formValues, setFormValues] = useState<Record<string, any>>({});
    const [formErr, setFormErr] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [playerId, setPlayerId] = useState<string | undefined>();
    const [gameReady, setGameReady] = useState(false);

    // Content modal (terms/privacy popup)
    const [contentModal, setContentModal] = useState<{ title: string; content?: string; url?: string } | null>(null);
    const showContentModal = (title: string, content?: string, url?: string) => setContentModal({ title, content, url });
    const closeContentModal = () => setContentModal(null);

    // Campaign config
    const requireFormBefore   = !isTemplate && (game?.campaign?.require_form_before_play ?? false);
    const requireFormAfterWin = !isTemplate && (game?.campaign?.require_form_after_win ?? false);
    const formFields: FormFieldDef[] = (game?.campaign?.form_fields?.length > 0)
        ? game.campaign.form_fields
        : DEFAULT_FIELDS;

    // Theme: đọc từ game instance config (game.config.theme) hoặc fallback về 'blue'
    const themeId: string = game?.config?.theme || game?.game_template?.config?.theme || 'blue';
    const theme = FORM_THEMES[themeId] || DEFAULT_THEME;

    const iframeRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        const fetchPreviewData = async () => {
            try {
                let res;
                if (isTemplate) {
                    // Template preview vẫn cần auth (chỉ admin dùng)
                    res = await api.getGameTemplate(id!);
                } else {
                    // Instance preview: dùng public endpoint — không cần auth
                    // Cho phép chia sẻ link preview với người ngoài
                    const r = await fetch(`${API_BASE}/public/v1/game-instances/${id}`);
                    if (!r.ok) throw new Error(`Không tìm thấy game instance (${r.status})`);
                    res = await r.json();
                }
                if (res) setGame(res);
            } catch (err: any) {
                setError(err.message || 'Error loading preview data');
            } finally {
                setLoading(false);
            }
        };
        fetchPreviewData();
    }, [id, isTemplate]);

    // Player status từ public API (cooldown / limit)
    const [playerStatus, setPlayerStatus] = useState<{
        canPlay: boolean;
        blockReason: string | null;
        blockMessage: string | null;
        suggestedAction: string;
        cooldownRemainSec: number;
    } | null>(null);

    useEffect(() => {
        if (!game) return;
        
        const templateCode = String(game?.game_template?.code || game?.code || '').toLowerCase();
        const isLuckyWheel = templateCode.includes('lucky') || templateCode.includes('wheel');
        
        // Chỉ những game hỗ trợ form nội bộ (như Lucky Wheel) mới bỏ qua parent form.
        // Các game khác (Flip Card, Scratch Card, Egg Pick) cần hiển thị parent React Form nếu có yêu cầu.
        if (!isLuckyWheel && requireFormBefore && !playerId) {
            setFormMode('before');
            setShowForm(true);
            setGameReady(false);
        } else {
            setGameReady(true);
        }
    }, [game, requireFormBefore, playerId]);

    useEffect(() => {
        const handleMessage = (e: MessageEvent) => {
            if (e.data?.source === 'xgame') {
                console.log("GamePreview received message:", e.data);
                const msgPlayerId = e.data?.playerId || e.data?.player_id;
                if (msgPlayerId) {
                    setPlayerId(msgPlayerId);
                }
                if (e.data?.type === 'game:win') {
                    if (requireFormAfterWin) {
                        setFormMode('after_win');
                        setShowForm(true);
                    }
                }
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [requireFormAfterWin]);

    const handleFieldChange = (key: string, val: any) => {
        setFormValues(prev => ({ ...prev, [key]: val }));
    };

    const handleFormSubmit = async () => {
        // Validate required fields
        const missing = formFields.filter(f => {
            if (!f.required) return false;
            const v = formValues[f.key];
            if (f.type === 'checkbox') return !v;
            return !v || String(v).trim() === '';
        });
        if (missing.length > 0) {
            setFormErr(`Vui lòng điền: ${missing.map(f => f.label).join(', ')}`);
            return;
        }

        setFormErr('');
        setFormLoading(true);
        try {
            const slug = game?.campaign?.slug;
            if (slug) {
                // Build payload: name, phone, email ở top level + metadata cho các field khác
                const { name, phone, email, ...rest } = formValues;
                const body: any = { name, phone, email };
                if (playerId) body.playerId = playerId;
                if (Object.keys(rest).length > 0) body.metadata = rest;

                const res = await fetch(`${API_BASE}/public/v1/campaigns/${slug}/players`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                const json = await res.json();
                const pid = json?.data?.playerId || json?.data?.player_id || json?.playerId;
                if (pid) setPlayerId(pid);
            }
            setShowForm(false);
            if (formMode === 'before') setGameReady(true);
        } catch (e: any) {
            setFormErr(e.message || 'Không thể lưu thông tin. Vui lòng thử lại.');
        } finally {
            setFormLoading(false);
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">Loading Simulator...</div>;

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-red-400">
            <div className="text-center">
                <p className="text-6xl mb-4">⚠️</p>
                <p className="text-xl">{error}</p>
                <p className="text-sm text-gray-500 mt-2">Cannot load SDK Previewer. Is the Game Instance ID correct?</p>
            </div>
        </div>
    );

    const srcUrl = gameReady ? buildPreviewUrl(game, isTemplate, playerId) : '';

    return (
        <div className="w-full h-screen bg-white relative overflow-hidden flex flex-col">
            {/* Screen */}
            <div className="flex-1 bg-white relative w-full h-full overflow-hidden">

                {/* CONTENT MODAL — terms/privacy popup */}
                {contentModal && (
                    <ContentModal
                        title={contentModal.title}
                        content={contentModal.content}
                        url={contentModal.url}
                        onClose={closeContentModal}
                    />
                )}

                {/* BLOCK OVERLAY — cooldown / limit_reached */}
                {playerStatus && !playerStatus.canPlay && (
                    <div className="absolute inset-0 z-50 flex flex-col items-center justify-center p-6 text-center"
                        style={{ background: theme.overlayBg || 'rgba(5,5,20,0.96)', backdropFilter: 'blur(12px)' }}>
                        <div className="w-full max-w-xs rounded-2xl p-6 shadow-xl"
                            style={{ background: theme.cardBg, border: `1.5px solid ${theme.cardBorder}` }}>
                            <p className="text-5xl mb-3">
                                {playerStatus.blockReason === 'limit_reached' ? '🚫' : '⏳'}
                            </p>
                            <h2 className="font-bold text-base mb-2" style={{ color: theme.title }}>
                                {playerStatus.blockReason === 'limit_reached' ? 'Bạn đã tham gia rồi!' : 'Hãy quay lại sau nhé!'}
                            </h2>
                            <p className="text-xs text-gray-300 opacity-90 mb-3 leading-relaxed">
                                {playerStatus.blockMessage}
                            </p>
                            {/* Countdown timer nếu cooldown */}
                            {playerStatus.cooldownRemainSec > 0 && (
                                <CountdownTimer
                                    initialSec={playerStatus.cooldownRemainSec}
                                    onExpire={() => window.location.reload()}
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* FORM OVERLAY — chỉ hiện khi được phép chơi, style theo theme game */}
                {showForm && !(playerStatus && !playerStatus.canPlay) && (
                    <div className="absolute inset-0 z-50 overflow-y-auto"
                        style={{ background: theme.overlayBg, backdropFilter: 'blur(8px)' }}>
                        <style>{`
                            .preview-form-input:focus {
                                border-color: ${theme.inputFocus} !important;
                                box-shadow: 0 0 0 1.5px ${theme.inputFocus} !important;
                            }
                        `}</style>
                        <div className="min-h-full flex items-center justify-center p-4">
                            <div className="w-full max-w-[600px] rounded-2xl p-6 shadow-2xl"
                                style={{ background: theme.cardBg, border: `1.5px solid ${theme.cardBorder}`, backdropFilter: 'blur(16px)' }}>

                                {/* Header */}
                                {formMode === 'before' ? (
                                    <>
                                        <p className="text-2xl text-center mb-1">📝</p>
                                        <h2 className="text-center font-bold text-base mb-1" style={{ color: theme.title }}>Nhập thông tin</h2>
                                        <p className="text-center text-xs text-gray-400 mb-4">Điền thông tin để bắt đầu</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-2xl text-center mb-1">🎁</p>
                                        <h2 className="text-center font-bold text-base mb-1" style={{ color: theme.title }}>Bạn đã trúng thưởng!</h2>
                                        <p className="text-center text-xs text-gray-400 mb-4">Điền thông tin để nhận quà</p>
                                    </>
                                )}

                                {/* Dynamic Fields */}
                                {formFields.map(field => (
                                    <DynamicField
                                        key={field.key}
                                        field={field}
                                        value={formValues[field.key]}
                                        onChange={handleFieldChange}
                                        onShowModal={showContentModal}
                                        theme={theme}
                                    />
                                ))}

                                {formErr && <p className="text-xs text-red-400 text-center mb-3">{formErr}</p>}

                                <button
                                    onClick={handleFormSubmit}
                                    disabled={formLoading}
                                    className="w-full py-3 rounded-xl font-bold text-sm mt-2"
                                    style={{
                                        background: theme.submitBg,
                                        color: theme.submitColor,
                                        opacity: formLoading ? 0.6 : 1
                                    }}
                                >
                                    {formLoading ? 'Đang xử lý...' : formMode === 'before' ? '🎯 Bắt đầu' : '✅ Nhận thưởng'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* GAME IFRAME */}
                {srcUrl ? (
                    <iframe
                        ref={iframeRef}
                        id="preview-frame"
                        src={srcUrl}
                        className="w-full h-full border-0 absolute inset-0"
                        title="Game Play"
                    />
                ) : (
                    !showForm && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center bg-gray-900">
                            <span className="text-5xl mb-4">🎮</span>
                            <p className="text-gray-400 text-sm">Không tìm thấy runtime_url.</p>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

